import React, { useState, forwardRef, useCallback } from 'react';
import EmptyState from 'components/empty-state';
import useSizeClass from 'hooks/use-size-class';
import ButtonGroup, { RENTED_BUTTON_ID, SOLD_BUTTON_ID } from './button-group/ButtonGroup';
import PriceHistoryRow from './price-history-row';
import ArrowIcon from 'components/icon/arrow-icon';
import styles from './style.module.scss';
import { listingPageModalIds } from 'constants/test-constants';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { Trans, useTranslation } from 'next-i18next';
import { ComplianceRequirement } from 'types/compliance';

import type { PriceHistoryRowListingData, PriceHistoryRowProps } from './price-history-row';
import type { ListingSerializableProperties } from 'data/listing';
import type Listing from 'data/listing';
import { getHighestComplianceRequirement } from 'utils/compliance';
import { useIsomorphicLayoutEffect } from 'types/react';

const NUMBER_OF_ROWS_BY_DEFAULT = 3;

//#region types

export type SaleHistory<T extends Listing | ListingSerializableProperties = Listing> = PriceHistoryRowListingData<T>;
export type RentalHistory<T extends Listing | ListingSerializableProperties = Listing> = PriceHistoryRowListingData<T>;
export type PriceHistory<T extends Listing | ListingSerializableProperties = Listing> = SaleHistory<T> | RentalHistory<T>;

export type PriceHistoryProps = {
  currentListingId: number;
  isRental: boolean;
  saleHistory?: readonly SaleHistory[];
  rentalHistory?: readonly RentalHistory[];
  onComplianceRequirementClick?: (complianceRequirement: ComplianceRequirement, providerId: number) => void;
  onContactForPriceClick?: () => void;
  onViewListingClick?: (addressPath: string, listingId: number) => void;
};

type HistoryTableProps = {
  currentListingId: number;
  size: number;
  priceHistory: PriceHistory[];
} & Pick<PriceHistoryRowProps, 'onContactForPriceClick' | 'onViewListingClick'>;

//#endregion

//#region sub-components
function RequireSignIn({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation(Namespaces.COMMON);
  return (
    <div className={styles['login-cta']}>
      <Trans
        t={t}
        i18nKey={LOCALE_COMMON_PATHS.COMPLIANCE_PRICE_HISTORY_SIGN_IN_REQUIRED}
        components={{ span: <span onClick={onClick} /> }}
        defaultValue={'Real estate boards require that you sign in to see the full details of this property. <span> Log in or sign up </span> to see price history.'}
      />
    </div>
  );
}

function RequireTermsOfUse({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation(Namespaces.COMMON);
  return (
    <div className={styles['login-cta']}>
      <Trans
        t={t}
        i18nKey={LOCALE_COMMON_PATHS.COMPLIANCE_PRICE_HISTORY_TERMS_OF_USE_REQUIRED}
        components={{ span: <span onClick={onClick} /> }}
        defaultValue={'Real estate boards require that you accept Terms of use to see the full details of this property. <span>Accept Terms of Use </span> to see price history.'}
      />
    </div>
  );
}

function ComplianceRequirementMessage({ complianceRequirement, onClick }: { complianceRequirement: ComplianceRequirement; onClick: () => void }) {
  if (complianceRequirement === ComplianceRequirement.SIGN_IN) {
    return <RequireSignIn onClick={onClick} />;
  } else if (complianceRequirement === ComplianceRequirement.TERMS_OF_USE) {
    return <RequireTermsOfUse onClick={onClick} />;
  }
  return null;
}

function HistoryTableDesktop(props: { isRental: boolean } & HistoryTableProps) {
  const {
    isRental = false,
    currentListingId,
    size = NUMBER_OF_ROWS_BY_DEFAULT,
    priceHistory = [],
    onContactForPriceClick,
    onViewListingClick,
  } = props;

  return (
    <>
      <HistoryTableHeader isRental={isRental} />
      {priceHistory.slice(0, size).map((listing: PriceHistory, index: React.Key) => (
        <PriceHistoryRow
          key={index}
          testId={`${listingPageModalIds.priceHistoryRow}-${index}`}
          listing={listing}
          isBlurred={listing.complianceRequirement !== ComplianceRequirement.NONE}
          isCurrentListing={listing.id === currentListingId}
          onContactForPriceClick={onContactForPriceClick}
          onViewListingClick={onViewListingClick}
        />
      ))}
    </>
  );
}

function HistoryTableMobile(props: HistoryTableProps) {
  const { currentListingId, size, priceHistory, onContactForPriceClick, onViewListingClick } = props;
  return (
    <>
      {priceHistory.slice(0, size).map((listing: PriceHistory, index: React.Key) => (
        <PriceHistoryRow
          key={index}
          testId={`${listingPageModalIds.priceHistoryRow}-${index}`}
          listing={listing}
          isBlurred={listing.complianceRequirement !== ComplianceRequirement.NONE}
          isCurrentListing={listing.id === currentListingId}
          onContactForPriceClick={onContactForPriceClick}
          onViewListingClick={onViewListingClick}
        />
      ))}
    </>
  );
  return null;
}

const HistoryTableHeader = ({ isRental }: { isRental: boolean }) => {
  const { t } = useTranslation(Namespaces.COMMON);
  return (
    <div className={`${styles.grid} ${styles.header}`}>
      <div>{t(LOCALE_COMMON_PATHS.PRICE_HISTORY_LIST_DATE_LABEL, { defaultValue: 'List Date' })}</div>
      <div>{t(LOCALE_COMMON_PATHS.PRICE_HISTORY_LIST_PRICE_LABEL, { defaultValue: 'List Price' })}</div>
      <div>{t(LOCALE_COMMON_PATHS.PRICE_HISTORY_STATUS_LABEL, { defaultValue: 'Status' })}</div>
      <div>{t(LOCALE_COMMON_PATHS.PRICE_HISTORY_SOLD_DATE_LABEL, { defaultValue: 'End Date' })}</div>
      <div>{isRental ?
        t(LOCALE_COMMON_PATHS.PRICE_HISTORY_RENTED_PRICE_LABEL, { defaultValue: 'Rent Price' }) :
        t(LOCALE_COMMON_PATHS.PRICE_HISTORY_SOLD_PRICE_LABEL, { defaultValue: 'Sold Price' })}</div>
      <div></div>
    </div>
  );
};

const HistorySizeToggleButton = ({ numberOfListings, onClick }: { numberOfListings: number; onClick: () => void }) => {
  const { t } = useTranslation(Namespaces.COMMON);
  return (
    <button onClick={onClick} className={`${styles['view-more']} ${numberOfListings > NUMBER_OF_ROWS_BY_DEFAULT && styles.less}`}>
      { numberOfListings <= NUMBER_OF_ROWS_BY_DEFAULT ?
        t(LOCALE_COMMON_PATHS.PRICE_HISTORY_VIEW_MORE_BUTTON_LABEL, { defaultValue: 'View More' }) :
        t(LOCALE_COMMON_PATHS.PRICE_HISTORY_VIEW_LESS_BUTTON_LABEL, { defaultValue: 'View Less' })}
      <ArrowIcon />
    </button>
  );
};

//#endregion

const PriceHistory = forwardRef<HTMLDivElement, PriceHistoryProps>((props, ref) => {
  const { t } = useTranslation(Namespaces.COMMON);
  const {
    saleHistory = [],
    rentalHistory = [],
    isRental,
    currentListingId,
    onComplianceRequirementClick,
    onContactForPriceClick,
    onViewListingClick,
  } = props;

  const onComplianceRequirementClickRef = React.useRef(onComplianceRequirementClick);
  const highestRequirement = getHighestComplianceRequirement([...saleHistory, ...rentalHistory]);

  useIsomorphicLayoutEffect(() => {
    onComplianceRequirementClickRef.current = onComplianceRequirementClick;
  }, [onComplianceRequirementClick]);

  const handleComplianceRequirementClick = useCallback(() => {
    onComplianceRequirementClickRef.current?.(highestRequirement.requirement, highestRequirement.providerId);
  }, [highestRequirement.requirement, highestRequirement.providerId]);

  // By default, we show 3 rows of history.
  const [numberOfRowsToDisplay, setNumberOfRowsToDisplay] = useState(NUMBER_OF_ROWS_BY_DEFAULT);

  // We only show the empty state if there are not sale or rental history
  const showNoHistoryAvailable = saleHistory.length === 0 && rentalHistory.length === 0;
  const totalNumberOfHistoryRows = saleHistory.length + rentalHistory.length;

  // We only show the toggle button if there are more than 3 listings and there is no compliance requirement.
  const showToggleNumberListings = totalNumberOfHistoryRows > NUMBER_OF_ROWS_BY_DEFAULT && highestRequirement.requirement === ComplianceRequirement.NONE;
  const isMobile = useSizeClass({ max: 1000 });
  const showButtonGroup = saleHistory.length > 0 && rentalHistory.length > 0;

  // Depending on the current number of rows being displayed, we toggle the displayable row count between 3 and the total number of history rows.
  const toggleNumberOfRowsToDisplay = () => setNumberOfRowsToDisplay(numberOfRowsToDisplay > NUMBER_OF_ROWS_BY_DEFAULT ? NUMBER_OF_ROWS_BY_DEFAULT : totalNumberOfHistoryRows);

  return (
    <div className={styles.component} data-testid={listingPageModalIds.priceHistorySection} ref={ref}>
      <h2 data-testid={listingPageModalIds.priceHistoryHeading}>
        {t(LOCALE_COMMON_PATHS.PRICE_HISTORY_HEADING_LABEL, { defaultValue: 'Price History' })}
      </h2>

      { showNoHistoryAvailable ?
        <EmptyState
          message={t(LOCALE_COMMON_PATHS.PRICE_HISTORY_NO_HISTORY_AVAILABLE, { defaultValue: 'No History Available' })}
        /> :
        <>
          {showButtonGroup && <ButtonGroup activeButton={isRental ? RENTED_BUTTON_ID : SOLD_BUTTON_ID} />}
          { isMobile ?
            <HistoryTableMobile
              currentListingId={currentListingId}
              size={numberOfRowsToDisplay}
              priceHistory={saleHistory.concat(rentalHistory)}
              onContactForPriceClick={onContactForPriceClick}
              onViewListingClick={onViewListingClick}
            /> :
            <HistoryTableDesktop
              isRental={isRental}
              currentListingId={currentListingId}
              size={numberOfRowsToDisplay}
              priceHistory={saleHistory.concat(rentalHistory)}
              onContactForPriceClick={onContactForPriceClick}
              onViewListingClick={onViewListingClick}
            />
          }
          { showToggleNumberListings &&
            <HistorySizeToggleButton numberOfListings={numberOfRowsToDisplay} onClick={toggleNumberOfRowsToDisplay} />
          }
          <ComplianceRequirementMessage complianceRequirement={highestRequirement.requirement} onClick={handleComplianceRequirementClick} />
        </>
      }
    </div>
  );
});

PriceHistory.displayName = 'PriceHistory';

export default PriceHistory;

